import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Select from 'react-select';
import { schema } from './Schema';
import { z } from 'zod';
import CreatableSelect from 'react-select/creatable';
import clsx from 'clsx';
import { BrandSector } from '../../../mappings';
import { toTitleCase } from '../../../../../../_metronic/helpers';
import React from 'react';
import InfoTooltip from '../../../../../modules/components/tooltip/InfoTooltip';
import PreviewDropzoneWithPaste from '../../../../../modules/components/dropzone/PreviewDropzoneWithPaste';

type BrandOption = {
  value: string;
  label: string;
  sector: string;
  __isNew__: boolean;
};

interface Props {
  defaultValues: z.infer<ReturnType<typeof schema>['addBrands']>['brands'][number];
  index: number;
  isLoading: boolean;
  onRemove: (index: number) => void;
  brandListOptions: any[];
  virtualMenu?: React.ReactNode;
}

const AddBrandsRow = React.memo(
  ({ defaultValues, index, isLoading, onRemove, brandListOptions, virtualMenu }: Props) => {
    const intl = useIntl();
    const acceptedFileTypes = {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    };
    const acceptedFileSize = 10 * 1024 * 1024;

    const { addBrands } = schema(intl);
    type FormDataType = z.infer<typeof addBrands>;

    const { control, watch, setValue } = useFormContext<FormDataType>();
    const selectedSector = watch(`brands.${index}.sector`);
    const selectedBrandName = watch(`brands.${index}.name`);
    const isNewBrand = watch(`brands.${index}.isNew`) ?? false;

    const sectorOptions = Object.keys(BrandSector).map((key) => ({
      value: key,
      label: toTitleCase(key.replaceAll('_', ' ')),
    }));
    const searchTypeOptions = [
      { value: 'EXACT_MATCH', label: 'Exact Match' },
      { value: 'PARTIAL', label: 'Partial' },
    ];

    // Build selectable options from all known brands so users can search by name first.
    const brandOptions = useMemo<BrandOption[]>(() => {
      if (!(brandListOptions?.length > 0)) return [];
      return brandListOptions.map((brand) => ({
        value: brand?.name,
        label: brand?.name,
        sector: brand?.sector,
        __isNew__: false,
      }));
    }, [brandListOptions]);

    const selectedBrandOption = useMemo<BrandOption | null>(() => {
      if (!selectedBrandName) return null;

      const matchedExistingBrand = brandOptions.find(
        (option) => option.value === selectedBrandName
      );

      if (matchedExistingBrand) {
        return { ...matchedExistingBrand, __isNew__: isNewBrand };
      }

      return {
        label: selectedBrandName,
        value: selectedBrandName,
        sector: selectedSector ?? defaultValues.sector,
        __isNew__: isNewBrand,
      };
    }, [brandOptions, defaultValues.sector, isNewBrand, selectedBrandName, selectedSector]);

    return (
      <div className='d-flex justify-content-between align-items-start gap-4 mb-1'>
        <div className='w-175px w-lg-225px'>
          {index === 0 && <label className='form-label m-0 required'>Brand Name</label>}
          <Controller
            name={`brands.${index}.name`}
            control={control}
            render={({ field, fieldState }) => {
              return (
                <CreatableSelect<BrandOption, false>
                  placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                  noOptionsMessage={() => intl.formatMessage({ id: 'COMMON.SELECT' })}
                  className={`w-100 ${fieldState.invalid ? 'border rounded border-danger' : ''}`}
                  options={brandOptions}
                  value={selectedBrandOption}
                  getNewOptionData={(inputValue) => ({
                    value: inputValue,
                    label: inputValue,
                    sector: '',
                    __isNew__: true,
                  })}
                  isValidNewOption={(inputValue) => {
                    const trimmedInput = inputValue?.trim();
                    if (!trimmedInput) return false;
                    return !brandOptions.some(
                      (option) => option.value.toLowerCase() === trimmedInput.toLowerCase()
                    );
                  }}
                  onChange={(e) => {
                    if (!e) {
                      setValue(`brands.${index}.isNew`, defaultValues.isNew);
                      setValue(`brands.${index}.sector`, defaultValues.sector);
                      setValue(`brands.${index}.searchType`, defaultValues.searchType);
                      field.onChange(defaultValues.name);
                      return;
                    }

                    const selectedOption = e as BrandOption;
                    const isNew = Boolean(selectedOption?.__isNew__);

                    setValue(`brands.${index}.isNew`, isNew);
                    field.onChange(selectedOption?.value ?? '');

                    if (isNew) {
                      // New brands require user-selected sector and search type.
                      setValue(`brands.${index}.searchType`, defaultValues.searchType);
                      return;
                    }

                    // Existing brands auto-populate sector from master list.
                    setValue(`brands.${index}.sector`, selectedOption?.sector ?? defaultValues.sector);
                    setValue(`brands.${index}.searchType`, defaultValues.searchType);
                  }}
                  isDisabled={isLoading}
                  // To prevent cutting-off dropdown when parent has overflow set
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              );
            }}
          />
        </div>

        <div className='w-125px w-lg-200px'>
          {index === 0 && <label className='form-label m-0 required'>Sector</label>}
          <Controller
            name={`brands.${index}.sector`}
            control={control}
            render={({ field, fieldState }) => {
              return (
                <Select
                  {...field}
                  placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                  className={`w-100 ${fieldState.invalid ? 'border rounded border-danger' : ''}`}
                  onChange={(e) => {
                    field.onChange(e?.value);
                  }}
                  options={sectorOptions}
                  value={sectorOptions.find((option) => option.value === field.value)}
                  isDisabled={!isNewBrand || isLoading}
                  // To prevent cutting-off dropdown when parent has overflow set
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              );
            }}
          />
        </div>

        <div className='w-100px w-lg-150px'>
          {index === 0 && (
            <label className='form-label m-0'>
              Search Type
              <InfoTooltip
                id='search_type_tooltip'
                placement='top'
                iconFontSize='fs-4'
                className='ps-2'
              >
                <p className='m-0'>
                  {searchTypeOptions.map((option, index) => (
                    <>
                      {index > 0 && <br />}
                      <strong>{option.label}</strong>
                      {option.value === 'EXACT_MATCH' ? (
                        <span>: Best for languages that use spaces (e.g., English).</span>
                      ) : (
                        <span>
                          : Best for languages written without spaces (e.g., Chinese, Japanese,
                          Korean).
                        </span>
                      )}
                    </>
                  ))}
                </p>
              </InfoTooltip>
            </label>
          )}
          <Controller
            name={`brands.${index}.searchType`}
            control={control}
            render={({ field, fieldState }) => {
              return (
                <Select
                  {...field}
                  placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                  className={`w-100 ${fieldState.invalid ? 'border rounded border-danger' : ''}`}
                  onChange={(e) => {
                    field.onChange(e?.value ?? '');
                  }}
                  options={searchTypeOptions}
                  value={searchTypeOptions.find((option) => option.value === field.value) ?? null}
                  isDisabled={!isNewBrand || isLoading}
                  // To prevent cutting-off dropdown when parent has overflow set
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              );
            }}
          />
        </div>

        <div className='w-150px w-lg-175px'>
          {index === 0 && <label className='form-label m-0 required'>Screenshot</label>}
          <Controller
            name={`brands.${index}.screenshot`}
            control={control}
            render={({ field, fieldState }) => {
              return (
                <PreviewDropzoneWithPaste
                  file={field.value instanceof File ? field.value : null}
                  onFileChange={(file) => {
                    field.onChange(file || null);
                  }}
                  acceptFileTypes={acceptedFileTypes}
                  maxSize={acceptedFileSize}
                  subLabel=''
                  error={fieldState.error?.message}
                  className={clsx(isLoading && 'opacity-75')}
                  previewItemWidth='100%'
                  previewItemHeight='75px'
                  hideErrorMessage='external'
                  isMini
                />
              );
            }}
          />
        </div>

        <div className='d-flex align-items-center'>
          <i
            className={clsx(
              'bi bi-dash-square-fill fs-1',
              index > 0 ? 'text-danger cursor-pointer' : 'mt-8'
            )}
            onClick={() => {
              if (index > 0) {
                onRemove(index);
              }
            }}
          ></i>
        </div>
      </div>
    );
  }
);
export { AddBrandsRow };
